/**
 * SS Market popup — <ss-market-popup>
 * Decides whether to suggest another market/language and drives the dialog.
 *
 * Detection (first-party only, nothing leaves Shopify):
 *  - country  : GET /browsing_context_suggestions.json?country[enabled]=true
 *               → detected_values.country.handle (Shopify's IP geolocation)
 *  - language : navigator.languages (browser preference order)
 * A mismatch is flagged only when the suggested country / language is one
 * the store actually offers (Shopify Markets). The visitor's choice or
 * dismissal is remembered in localStorage for `expiryDays`.
 * Public hooks: any element with [data-ss-market-popup-open] opens it;
 * events `ss:market-popup:open|close|submit` are dispatched on the element.
 */
(function () {
  'use strict';
  if (customElements.get('ss-market-popup')) return;

  var STORE_KEY = 'ss:market-popup';
  var SESSION_KEY = 'ss:market-popup:shown';

  function storage(kind) {
    try { var s = window[kind]; s.setItem('__ss', '1'); s.removeItem('__ss'); return s; } catch (e) { return null; }
  }
  function readJSON(s, key) { try { return JSON.parse(s.getItem(key) || 'null'); } catch (e) { return null; } }
  function flagEmoji(code) {
    if (!/^[A-Z]{2}$/.test(code)) return '';
    return String.fromCodePoint.apply(null, code.split('').map(function (c) { return 127397 + c.charCodeAt(0); }));
  }
  function fill(template, map) {
    return String(template || '').replace(/%(COUNTRY|LANGUAGE|CURRENCY|CURRENT_LANGUAGE)%/g, function (_, k) { return map[k] != null ? map[k] : ''; });
  }
  function primary(code) { return String(code || '').toLowerCase().split('-')[0]; }

  class SsMarketPopup extends HTMLElement {
    constructor() { super(); this._listeners = []; }

    connectedCallback() {
      if (window.location.pathname === '/challenge') return;
      var cfgEl = this.querySelector('[data-ss-market-config]');
      try { this.cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) { this.cfg = null; }
      if (!this.cfg) return;
      if (this.cfg.strings) Object.keys(this.cfg.strings).forEach(function (k) {
        var v = this.cfg.strings[k];
        if (typeof v === 'string') this.cfg.strings[k] = v.replace(/&(#39|quot|lt|gt|amp);/g, function (m, e) { return { '#39': "'", quot: '"', lt: '<', gt: '>', amp: '&' }[e]; });
      }, this);
      this.local = storage('localStorage'); this.session = storage('sessionStorage');
      this.dialog = this.querySelector('[data-ss-market-dialog]');
      this.heading = this.querySelector('[data-ss-market-heading]');
      this.text = this.querySelector('[data-ss-market-text]');
      this.flag = this.querySelector('[data-ss-market-flag]');
      this.currentLine = this.querySelector('[data-ss-market-current]');
      this.form = this.querySelector('[data-ss-market-form]');
      this.countrySelect = this.querySelector('[data-ss-market-country]');
      this.languageSelect = this.querySelector('[data-ss-market-language]');
      this.currencyOut = this.querySelector('[data-ss-market-currency]');
      this.stayBtn = this.querySelector('[data-ss-market-dismiss].ss-market-popup__stay');
      this.suggest = { country: null, language: null };

      this.querySelectorAll('[data-ss-market-dismiss]').forEach(function (el) { this.on(el, 'click', this.dismiss.bind(this)); }, this);
      if (this.countrySelect) this.on(this.countrySelect, 'change', this.syncCurrency.bind(this));
      if (this.form) this.on(this.form, 'submit', this.onSubmit.bind(this));
      this.on(document, 'keydown', this.onKeydown.bind(this));
      this.on(document, 'click', function (e) {
        var opener = e.target.closest && e.target.closest('[data-ss-market-popup-open]');
        if (opener) { e.preventDefault(); this.open({ manual: true }); }
      }.bind(this));

      if (this.cfg.designMode) {
        this.on(document, 'shopify:section:select', function (e) { if (e.detail && e.detail.sectionId === this.cfg.sectionId) this.open({ manual: true }); }.bind(this));
        this.on(document, 'shopify:section:deselect', function (e) { if (e.detail && e.detail.sectionId === this.cfg.sectionId) this.close(); }.bind(this));
        if (this.cfg.mode === 'testing') this.open({ manual: true });
        return;
      }
      this.syncCurrency();
      this.evaluate();
    }

    disconnectedCallback() { this._listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2]); }); this._listeners = []; }
    on(el, type, fn) { el.addEventListener(type, fn); this._listeners.push([el, type, fn]); }

    /* ---------- decision ---------- */
    evaluate() {
      var cfg = this.cfg;
      if (cfg.mode === 'testing') { this.detectCountry().then(function () { this.pickLanguage(); this.open(); }.bind(this)); return; }
      if (navigator.webdriver) return;
      var remembered = this.local && readJSON(this.local, STORE_KEY);
      if (remembered && remembered.until > Date.now()) return;
      if (this.session && this.session.getItem(SESSION_KEY)) return;

      var pending = cfg.checkCountry ? this.detectCountry() : Promise.resolve();
      pending.then(function () {
        if (cfg.checkLanguage) this.pickLanguage();
        if (this.suggest.country || this.suggest.language) {
          setTimeout(this.open.bind(this), cfg.delay || 0);
        }
      }.bind(this));
    }

    detectCountry() {
      var cfg = this.cfg;
      var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 3500) : null;
      return fetch('/browsing_context_suggestions.json?country[enabled]=true', { headers: { Accept: 'application/json' }, credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          var c = data && data.detected_values && data.detected_values.country;
          var code = c && String(c.handle || c.iso_code || '').toUpperCase();
          if (!code || code === cfg.current.country) return;
          var match = cfg.countries.find(function (x) { return x.code === code; });
          if (match) this.suggest.country = match;
        }.bind(this))
        .catch(function () { /* geolocation unavailable: silently skip */ })
        .then(function () { if (timer) clearTimeout(timer); });
    }

    pickLanguage() {
      var cfg = this.cfg, langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
      if (!langs || !langs[0]) return;
      var currentPrimary = primary(cfg.current.language);
      for (var i = 0; i < langs.length; i++) {
        var want = String(langs[i]).toLowerCase();
        var exact = cfg.languages.find(function (l) { return l.code.toLowerCase() === want; });
        var loose = exact || cfg.languages.find(function (l) { return primary(l.code) === primary(want); });
        if (loose) {
          if (primary(loose.code) !== currentPrimary) this.suggest.language = loose;
          return;                       // first browser language the store can serve decides
        }
      }
    }

    /* ---------- dialog ---------- */
    open(opts) {
      opts = opts || {};
      if (this.classList.contains('is-open')) return;
      var cfg = this.cfg, s = cfg.strings;
      var country = this.suggest.country, language = this.suggest.language;
      var map = {
        COUNTRY: country ? country.name : cfg.current.countryName,
        CURRENCY: country ? country.currency + ' ' + (country.symbol || '') : cfg.current.currency,
        LANGUAGE: language ? language.name : cfg.current.languageName,
        CURRENT_LANGUAGE: cfg.current.languageName
      };
      var languageOnly = !country && !!language;
      this.submitBtn = this.submitBtn || this.querySelector('[data-ss-market-submit]');
      if (this.heading && !opts.manual) {
        this.heading.textContent = country ? fill(s.headingCountry, map) : language ? fill(s.headingLanguage, map) : s.headingDefault;
      }
      if (this.text && !opts.manual) {
        this.text.textContent = country ? fill(s.textCountry, map) : language ? fill(s.textLanguage, map) : s.textDefault;
      }
      if (this.flag) {
        var emoji = flagEmoji(country ? country.code : cfg.current.country);
        if (emoji) this.flag.textContent = emoji;
      }
      if (this.countrySelect && country) this.countrySelect.value = country.code;
      if (this.languageSelect && language) this.languageSelect.value = language.code;
      if (this.stayBtn) this.stayBtn.textContent = languageOnly && !opts.manual ? fill(s.stayLanguage, map) : fill(s.stay, { COUNTRY: cfg.current.countryName });
      if (this.submitBtn) this.submitBtn.textContent = languageOnly && !opts.manual ? fill(s.submitLanguage, map) : s.submit;
      this.syncCurrency();

      this.lastFocus = document.activeElement;
      this.removeAttribute('hidden');
      void this.dialog.offsetWidth;           /* force a layout pass so the transition plays; no rAF (throttled in background tabs) */
      this.classList.add('is-open');
      document.body.classList.add('ss-market-popup-open');
      if (this.session) { try { this.session.setItem(SESSION_KEY, '1'); } catch (e) {} }
      var first = this.querySelector('select, button.ss-market-popup__submit');
      setTimeout(function () { (first || this.dialog).focus({ preventScroll: true }); }.bind(this), 50);
      this.dispatchEvent(new CustomEvent('ss:market-popup:open', { bubbles: true, detail: { suggest: this.suggest } }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.body.classList.remove('ss-market-popup-open');
      setTimeout(function () { this.setAttribute('hidden', ''); }.bind(this), 300);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('ss:market-popup:close', { bubbles: true }));
    }

    dismiss() { this.remember(this.cfg.current.country, this.cfg.current.language); this.close(); }

    remember(country, language) {
      if (!this.local) return;
      try { this.local.setItem(STORE_KEY, JSON.stringify({ country: country, language: language, until: Date.now() + (this.cfg.expiryDays || 30) * 864e5 })); } catch (e) {}
    }

    onSubmit() {
      var c = this.countrySelect ? this.countrySelect.value : this.cfg.current.country;
      var l = this.languageSelect ? this.languageSelect.value : this.cfg.current.language;
      this.remember(c, l);
      this.dispatchEvent(new CustomEvent('ss:market-popup:submit', { bubbles: true, detail: { country: c, language: l } }));
      /* native form submission continues: Shopify switches market + language and returns to this page */
    }

    syncCurrency() {
      if (!this.currencyOut || !this.countrySelect) return;
      var opt = this.countrySelect.options[this.countrySelect.selectedIndex];
      if (opt) this.currencyOut.textContent = (opt.dataset.currency || '') + (opt.dataset.symbol ? ' ' + opt.dataset.symbol : '');
    }

    onKeydown(e) {
      if (!this.classList.contains('is-open')) return;
      if (e.key === 'Escape') { e.preventDefault(); this.dismiss(); return; }
      if (e.key !== 'Tab') return;
      var f = Array.prototype.filter.call(this.dialog.querySelectorAll('button, select, [href], input, [tabindex]:not([tabindex="-1"])'), function (el) { return !el.disabled && el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  customElements.define('ss-market-popup', SsMarketPopup);
})();
